<script setup lang="ts">
import { ECOMMERCE_ITEM_BENEFIT_TYPE, ECOMMERCE_ITEM_PRICE_TYPE, type RewardLiveOp } from '@/types/shared/player-config'
import TheBottomSheet from '@/components/TheBottomSheet.vue'
import StarsBalancePill from '@/components/StarsBalancePill.vue'
import { useEcommerceItemDetailsPurchase } from '@/composables/game/ecommerce'
import { computed, ref } from 'vue'
import { useConfig } from '@/composables/game/config'
import { formatUnshortenedNumber } from '@/helpers/utils'
import { T } from '@tolgee/vue'
import ThePremium from '@/components/ThePremium.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import TonDiscountBadge from '@/components/TonDiscountBadge.vue'
import type { PuzzlePiece } from '@/composables/game/puzzles'

// Import puzzle piece images
import puzzleElement1 from '@/assets/images/puzzles/puzzle_element1.png'
import puzzleElement2 from '@/assets/images/puzzles/puzzle_element2.png'
import puzzleElement3 from '@/assets/images/puzzles/puzzle_element3.png'
import puzzleElement4 from '@/assets/images/puzzles/puzzle_element4.png'
import puzzleElement5 from '@/assets/images/puzzles/puzzle_element5.png'
import puzzleElement6 from '@/assets/images/puzzles/puzzle_element6.png'
import puzzleElement7 from '@/assets/images/puzzles/puzzle_element7.png'
import puzzleElement8 from '@/assets/images/puzzles/puzzle_element8.png'
import puzzleElement9 from '@/assets/images/puzzles/puzzle_element9.png'
import puzzleElement10 from '@/assets/images/puzzles/puzzle_element10.png'
import puzzleElement11 from '@/assets/images/puzzles/puzzle_element11.png'
import puzzleElement12 from '@/assets/images/puzzles/puzzle_element12.png'

const props = defineProps<{
	show: boolean
	piece: PuzzlePiece
	liveOp: RewardLiveOp
}>()

const emit = defineEmits<{
	(e: 'hide'): void
	(e: 'purchaseDone'): void
}>()

const { config } = useConfig()

// Map of puzzle piece images
const puzzleImages: Record<number, string> = {
  1: puzzleElement1,
  2: puzzleElement2,
  3: puzzleElement3,
  4: puzzleElement4,
  5: puzzleElement5,
  6: puzzleElement6,
  7: puzzleElement7,
  8: puzzleElement8,
  9: puzzleElement9,
  10: puzzleElement10,
  11: puzzleElement11,
  12: puzzleElement12,
}

const puzzleImageSrc = computed(() => puzzleImages[props.piece.stepNumber] || puzzleElement1)

// Find the ecommerce item for this reward live op step
const maybeEcommerceItem = computed(() => {
  return config.value.game.ecommerce.items.find(item =>
    item.benefits.some(b =>
      b.type === ECOMMERCE_ITEM_BENEFIT_TYPE.REWARD_LIVE_OP_STEP &&
			b.eventId === props.liveOp.id &&
			b.stepNumber === props.piece.stepNumber,
    ),
  )
})

const ecommerceItem = computed(() => {
  if (!maybeEcommerceItem.value) throw new Error(`Ecommerce item not found for reward live op ${props.liveOp.id} step ${props.piece.stepNumber}`)
  return maybeEcommerceItem.value
})

const {
  starPrice,
  tonPrice,
  isBuyingWithStars,
  isBuyingWithTon,
  showTonPurchase,
  onPurchase,
  selectedPriceType,
} = useEcommerceItemDetailsPurchase(ecommerceItem.value)

const isLoading = ref(false)
const purchased = ref(false)

async function _onPurchase(priceType: ECOMMERCE_ITEM_PRICE_TYPE) {
  isLoading.value = true
  try {
    const paid = await onPurchase(priceType, { type: 'puzzle', step: props.piece.stepNumber })
    if (paid) {
      purchased.value = true
    } else {
      isLoading.value = false
      selectedPriceType.value = null
    }
  } catch (e) {
    isLoading.value = false
    selectedPriceType.value = null
  }
}

function onClose() {
  if (purchased.value) {
    emit('purchaseDone')
  }
  emit('hide')
}

</script>

<template>
  <TheBottomSheet
    :show="show"
    bg-fade-class="z-[802]"
    wrapper-class="z-[802]"
    content-class="pt-6"
    @hide="onClose"
  >
    <div class="w-full relative pt-6">
      <StarsBalancePill class="absolute left-0 top-0" />

      <!-- Puzzle piece visual -->
      <div class="mx-auto max-w-fit">
        <div class="w-[120px] h-[120px] relative mx-auto">
          <!-- Puzzle piece image -->
          <img
            :src="puzzleImageSrc"
            :alt="`Puzzle piece ${piece.stepNumber}`"
            class="w-full h-full object-contain"
          >
        </div>
      </div>

      <Transition
        name="fade"
        mode="out-in"
      >
        <div
          v-if="purchased"
          class="mt-3 w-full flex flex-col items-center"
        >
          <div class="text-center text-light-white text-[24px] leading-8 font-bold -tracking-[0.48px]">
            Purchase successful!
          </div>
          <div class="text-center text-white/[.60] text-sm font-medium">
            Puzzle Piece = ${{ starPrice.usdAmount.toFixed(2) }}
          </div>
          <BaseButton
            class="mt-12 w-full"
            @click="onClose"
          >
            Close
          </BaseButton>
        </div>

        <div
          v-else
          class="mt-6 w-full flex flex-col items-center"
        >
          <div class="text-center text-light-white text-[24px] leading-8 font-bold -tracking-[0.48px]">
            Puzzle Piece = ${{ starPrice.usdAmount.toFixed(2) }}
          </div>

          <div class="mt-12 w-full flex flex-col gap-y-3">
            <BaseButton
              class="w-full"
              :loading="isBuyingWithStars"
              @click="_onPurchase(ECOMMERCE_ITEM_PRICE_TYPE.STAR)"
            >
              <span
                v-if="!isBuyingWithStars"
                class="flex items-center gap-x-2"
              >
                <T
                  key-name="starter_pack.buy_with"
                  default-value="Buy with"
                />
                <ThePremium :with-tooltip="false" />
                <span class="-ml-0.5">{{ formatUnshortenedNumber(starPrice.amount) }}</span>
              </span>
              <T
                v-else
                key-name="lingo.giveaway.transaction.loading_cta"
                default-value="Processing..."
              />
            </BaseButton>

            <template v-if="showTonPurchase">
              <div class="relative w-full">
                <div class="absolute right-12 -top-1 rotate-[20deg]">
                  <TonDiscountBadge label="-10%" />
                </div>
                <BaseButton
                  style-type="tertiary"
                  :loading="isBuyingWithTon"
                  class="w-full"
                  @click="_onPurchase(ECOMMERCE_ITEM_PRICE_TYPE.TON)"
                >
                  <span
                    v-if="!isBuyingWithTon"
                    class="flex items-center gap-x-2"
                  >
                    <T
                      key-name="starter_pack.buy_with"
                      default-value="Buy with"
                    />
                    <img
                      src="@/assets/images/ton-symbol.png"
                      alt="TON"
                      class="size-5 min-w-[20px] object-cover"
                    >
                    <span>{{ tonPrice?.amount }}</span>
                  </span>
                  <T
                    v-else
                    key-name="lingo.giveaway.transaction.loading_cta"
                    default-value="Processing..."
                  />
                </BaseButton>
              </div>

              <div
                class="mx-auto -mt-1 max-w-[307px] text-center text-sm -tracking-[0.28px] text-white/[.87] font-medium"
              >
                <T
                  key-name="transaction.process_description"
                  default-value="Transaction may take a few minutes to process. You'll be notified once it's successful."
                />
              </div>
            </template>
          </div>
        </div>
      </Transition>
    </div>
  </TheBottomSheet>
</template>

<style scoped>
</style>